import React from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Autocomplete,
  Chip,
  Button,
  Tooltip,
  useTheme,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

const quickSelectConfig = [
  { label: 'Today', color: '#2196f3' },
  { label: 'Last Week', color: '#ff9800' },
  { label: 'This Month', color: '#4caf50' },
  { label: 'Last Month', color: '#f44336' },
];

const QuickSelectChip = ({ label, color, onClick, mobile }) => (
  <Chip
    label={label}
    onClick={onClick}
    variant="outlined"
    size={mobile ? undefined : 'small'}
    sx={{
      cursor: 'pointer',
      borderColor: color,
      color: color,
      ...(mobile && { borderRadius: '100px', height: 30, flex: 1, fontSize: '0.9rem' }),
      transition: 'all 0.2s ease',
      '&:hover, &:focus': {
        backgroundColor: `${color} !important`,
        color: '#ffffff !important',
        borderColor: color,
      },
    }}
  />
);

const SearchFilters = ({
  customers,
  customer,
  setCustomer,
  customersLoading,
  addresses,
  address,
  setAddress,
  addressesLoading,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  onSearch,
  onToday,
  onLastWeek,
  onCurrentMonth,
  onLastMonth,
  reportLoading,
  hasUnappliedChanges,
}) => {
  const theme = useTheme();
  const quickHandlers = [onToday, onLastWeek, onCurrentMonth, onLastMonth];

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper',
        flex: 1,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, height: '100%' }}>
        {/* Row 1: Client & Location */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
          <Autocomplete
            options={customers}
            getOptionLabel={(option) => option.Name || ''}
            value={customer}
            onChange={(e, newValue) => setCustomer(newValue)}
            loading={customersLoading}
            isOptionEqualToValue={(option, value) => option.Id === value?.Id}
            sx={{ flex: 1, minWidth: { xs: '100%', md: 200 } }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Client"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {customersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Tooltip title={!customer ? "Please select a client first" : ""} arrow>
            <span style={{ flex: 1, minWidth: 200 }}>
              <Autocomplete
                options={addresses}
                getOptionLabel={(option) => option.Name || ''}
                value={address}
                onChange={(e, newValue) => setAddress(newValue)}
                loading={addressesLoading}
                disabled={!customer}
                isOptionEqualToValue={(option, value) => option.Id === value?.Id}
                fullWidth
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Location"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {addressesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </span>
          </Tooltip>
        </Box>

        {/* Row 2: Date Range, Quick Select, Search */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={(newValue) => setStartDate(newValue)}
            renderInput={(params) => <TextField {...params} sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)', md: 170 } }} />}
          />
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={(newValue) => setEndDate(newValue)}
            renderInput={(params) => <TextField {...params} sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)', md: 170 } }} />}
          />

          {/* Desktop Quick Select */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" fontWeight={500} whiteSpace="nowrap">
              Quick Select:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <QuickSelectChip label="Today" color="#2196f3" onClick={onToday} />
              <QuickSelectChip label="Last Week" color="#ff9800" onClick={onLastWeek} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <QuickSelectChip label="This Month" color="#4caf50" onClick={onCurrentMonth} />
              <QuickSelectChip label="Last Month" color="#f44336" onClick={onLastMonth} />
            </Box>
          </Box>

          <Box sx={{ display: { xs: 'none', md: 'block' }, flex: 1 }} />

          {/* Mobile Quick Select */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1, width: '100%' }}>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Quick Select:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {quickSelectConfig.map((config, i) => (
                <QuickSelectChip key={config.label} label={config.label} color={config.color} onClick={quickHandlers[i]} mobile />
              ))}
            </Box>
          </Box>

          {/* Search Button */}
          <Tooltip title={(!customer || !address) ? "Select/change location or date" : ""} arrow>
            <Box sx={{ width: { xs: '100%', md: 'auto' } }}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={onSearch}
                disabled={!customer || !address || reportLoading}
                sx={{
                  height: 48,
                  px: 3,
                  width: '100%',
                  bgcolor: hasUnappliedChanges ? 'primary.main' : 'grey.400',
                  '&:hover': {
                    bgcolor: hasUnappliedChanges ? 'primary.dark' : 'grey.500',
                  },
                  ...(hasUnappliedChanges && {
                    animation: 'pulse 2s infinite',
                    '@keyframes pulse': {
                      '0%': { boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.7)' },
                      '70%': { boxShadow: '0 0 0 10px rgba(25, 118, 210, 0)' },
                      '100%': { boxShadow: '0 0 0 0 rgba(25, 118, 210, 0)' },
                    },
                  }),
                }}
              >
                {reportLoading ? 'Loading...' : 'Search'}
              </Button>
            </Box>
          </Tooltip>
        </Box>
      </Box>
    </Paper>
  );
};

export default SearchFilters;
